import { type SetStateAction, type Dispatch, useMemo, useState } from 'react';
import { Search } from 'lucide-react';
import {
  MIN_PRODUCT_SEARCH_LENGTH,
  usePlatformProducts,
} from '~/api/platformProducts.tsx';
import { useDebouncedSearchInput } from '~/hooks/useDebouncedSearchInput';
import { ModalHeader } from '~/components/overlays';
import {
  useGetPlatformProductsLengthsForProduct,
  useUpsertInventory,
} from '~/api/inventory.tsx';
import { Button } from '~/components/ui';
import { useUpsertBuyerInventoryConfiguration } from '~/api/buyerInventoryConfiguration.tsx';
import { useGetLocations } from '~/api/location.tsx';
import { useTranslation } from 'react-i18next';
import { isNonNegativeNumber } from '~/utils/inventory.tsx';
import { formatLengthWithBasis, productLabel } from '~/utils';
import { LocationSelect } from '~/components/common/LocationSelect';
import { type UnitCode } from '~/graphql/graphql.ts';

type AddInventoryModalProps = {
  readonly setShowAddInventory: Dispatch<SetStateAction<boolean>>;
  readonly onSaved?: () => void;
};

function PlatformProductLengths({
  productId,
  onSelect,
}: Readonly<{
  productId: string;
  onSelect: (platformProductsLengthId: string) => void;
}>) {
  const {
    data: lengthsData,
    isLoading,
    error,
  } = useGetPlatformProductsLengthsForProduct(productId);

  const lengths = Array.isArray(lengthsData?.platformProductsLengthsForProduct)
    ? lengthsData.platformProductsLengthsForProduct
    : [];

  const selectPlaceholder = (() => {
    if (isLoading) return 'Loading lengths...';
    if (error) return 'Failed to load lengths';
    if (lengths.length === 0) return 'No lengths available';
    return 'Select a length';
  })();

  return (
    <div>
      <label
        htmlFor="length-select"
        className="block text sm font-medium text-neut-700 mb-2"
      >
        Length <span className="text-error-500">*</span>
      </label>
      <select
        id="length-select"
        className="w-full px-4 py-2 border border-neut-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-prim bg-neut-0"
        disabled={isLoading || !!error || lengths.length === 0}
        onChange={(e) => onSelect(e.target.value)}
      >
        <option value="">{selectPlaceholder}</option>
        {lengths.map((l) => (
          <option
            key={l.id}
            value={l.id}
            data-inventory-only={l.inventoryOnly ? 'true' : 'false'}
          >
            {formatLengthWithBasis(l.lengthDisplayValue, l.isNominal)}
          </option>
        ))}
      </select>
    </div>
  );
}

export function AddInventoryModal({
  setShowAddInventory,
  onSaved,
}: AddInventoryModalProps) {
  const { searchInputValue, activeSearch, handleSearchChange, setSearch } =
    useDebouncedSearchInput();
  const [openList, setOpenList] = useState(false);
  const { data, isFetching, error } = usePlatformProducts(activeSearch, {
    enabled: openList,
  });
  const searchTooShort =
    searchInputValue.trim().length < MIN_PRODUCT_SEARCH_LENGTH;
  const searchSettling =
    isFetching || activeSearch.trim() !== searchInputValue.trim();
  const {
    data: locationsData,
    isLoading: locationsLoading,
    error: locationsError,
  } = useGetLocations();
  const { mutate: mutateInventory } = useUpsertInventory();
  const { mutate: mutateBuyerInventoryConfiguration } =
    useUpsertBuyerInventoryConfiguration();
  const [selectedProductId, setSelectedProductId] = useState<string>('');
  const [selectedProductStandardUnitCode, setSelectedProductStandardUnitCode] =
    useState<UnitCode | ''>('');
  const [selectedProductLengthId, setSelectedProductLengthId] =
    useState<string>('');
  const [selectedLocationId, setSelectedLocationId] = useState<string>('');
  const [currentStock, setCurrentStock] = useState<number | ''>('');
  const [avgWeeklyConsumption, setAvgWeeklyConsumption] = useState<number | ''>(
    ''
  );

  const products = useMemo(
    () => (Array.isArray(data?.platformProducts) ? data.platformProducts : []),
    [data]
  );
  const locations = Array.isArray(locationsData?.locations)
    ? locationsData.locations
    : [];

  type Product = (typeof products)[number];
  const handleChoose = (p: Product) => {
    setSelectedProductId(p.id);
    setSelectedProductStandardUnitCode(p.standardUnitsCode);
    setSearch(p.name ?? '');
    setOpenList(false);
    setSelectedProductLengthId('');
  };

  const hasValidProductId =
    selectedProductId.trim().length > 0 &&
    selectedProductId !== 'null' &&
    selectedProductId !== 'undefined';

  const canSubmit =
    hasValidProductId &&
    !!selectedProductLengthId &&
    !!selectedLocationId &&
    isNonNegativeNumber(currentStock) &&
    isNonNegativeNumber(avgWeeklyConsumption);

  const onAddInventory = () => {
    if (!canSubmit) return;

    mutateInventory(
      {
        platformProductsLengthId: selectedProductLengthId,
        locationId: selectedLocationId,
        quantity: {
          value: Number(currentStock),
          unit: selectedProductStandardUnitCode as UnitCode,
        },
      },
      {
        onSuccess: () => {
          mutateBuyerInventoryConfiguration(
            {
              platformProductsLengthId: selectedProductLengthId,
              locationId: selectedLocationId,
              usageRatePerWeek: {
                value: Number(avgWeeklyConsumption),
                unit: selectedProductStandardUnitCode as UnitCode,
              },
            },
            {
              onSuccess: () => {
                onSaved?.();
                setShowAddInventory(false);
              },
            }
          );
        },
      }
    );
  };

  const { t } = useTranslation('inventory');
  return (
    <div className="bg-neut-0 rounded-lg shadow-xl max-w-xl w-full">
      <ModalHeader
        title={t('inventory.add')}
        description="Create a new inventory item"
        onClose={() => setShowAddInventory(false)}
      />

      <div className="p-6 space-y-4">
        <div className="relative">
          <label
            htmlFor="product-search"
            className="block text-sm font-medium text-neut-700 mb-2"
          >
            {t('inventory.product_name')}{' '}
            <span className="text-error-500">*</span>
          </label>

          <div className="relative">
            <Search
              size={16}
              strokeWidth={2}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-neut-400 pointer-events-none z-10"
              aria-hidden="true"
            />
            <input
              id="product-search"
              type="text"
              placeholder={t('inventory.select_product_placeholder')}
              className="w-full pl-10 pr-4 py-2 border border-neut-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-prim text-sm"
              value={searchInputValue}
              onFocus={() => setOpenList(true)}
              onBlur={() => setTimeout(() => setOpenList(false), 100)}
              onChange={(e) => {
                handleSearchChange(e);
                setOpenList(true);
              }}
            />
          </div>

          {openList && (
            <div className="mt-2 border border-neut-200 rounded-lg max-h-40 overflow-auto bg-neut-0">
              {error ? (
                <div className="px-3 py-2 text-sm text-error-500">
                  {t('inventory.search_failed')}
                </div>
              ) : searchTooShort ? (
                <div className="px-3 py-2 text-sm text-neut-500">
                  {t('inventory.keep_typing_to_search', {
                    count: MIN_PRODUCT_SEARCH_LENGTH,
                  })}
                </div>
              ) : products.length === 0 ? (
                <div className="px-3 py-2 text-sm text-neut-500">
                  {searchSettling
                    ? t('inventory.products_loading')
                    : t('inventory.no_products')}
                </div>
              ) : (
                products.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    className={`w-full text-left px-3 py-2 text-sm hover:bg-neut-50 ${selectedProductId === p.id ? 'bg-neut-50' : ''}`}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => handleChoose(p)}
                    title={productLabel(p)}
                  >
                    <span className="font-medium text-neut-800">
                      {productLabel(p)}
                    </span>
                  </button>
                ))
              )}
            </div>
          )}
        </div>

        {!hasValidProductId ? (
          <div>
            <label
              htmlFor="length-select-disabled"
              className="block text-sm font-medium text-neut-700 mb-2"
            >
              {t('inventory.length')} <span className="text-error-500">*</span>
            </label>
            <select
              id="length-select-disabled"
              className="w-full px-4 py-2 border border-neut-300 rounded-lg bg-neut-0"
              disabled
            >
              <option value="">{t('inventory.select_product')}</option>
            </select>
          </div>
        ) : (
          <PlatformProductLengths
            productId={selectedProductId}
            onSelect={setSelectedProductLengthId}
          />
        )}

        <LocationSelect
          label={t('inventory.location')}
          name="location"
          required
          value={selectedLocationId}
          onChange={(_, value) => setSelectedLocationId(value)}
          locations={locations}
          getLabel={(location) => location.name}
          isLoading={locationsLoading}
          error={locationsError}
        />

        <div>
          <label
            htmlFor="current-stock"
            className="block text-sm font-medium text-neut-700 mb-2"
          >
            {t('inventory.stock')}
            {selectedProductStandardUnitCode && (
              <span className="text-neut-500">
                {' '}
                ({selectedProductStandardUnitCode})
              </span>
            )}{' '}
            <span className="text-error-500">*</span>
          </label>
          <input
            id="current-stock"
            type="number"
            min={0}
            className="w-full px-4 py-2 border border-neut-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-prim"
            placeholder={t('inventory.stock_placeholder')}
            value={currentStock}
            onChange={(e) => {
              const v = e.target.value;
              setCurrentStock(v === '' ? '' : Number(v));
            }}
          />
        </div>

        <div>
          <label
            htmlFor="avg-weekly"
            className="block text-sm font-medium text-neut-700 mb-2"
          >
            {t('inventory.average_consumption')}
            {selectedProductStandardUnitCode && (
              <span className="text-neut-500">
                {' '}
                ({selectedProductStandardUnitCode})/{t('inventory.week')}
              </span>
            )}
            <span className="text-error-500">*</span>
          </label>
          <input
            id="avg-weekly"
            type="number"
            min={0}
            className="w-full px-4 py-2 border border-neut-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-prim"
            placeholder={t('inventory.average_consumption_placeholder')}
            value={avgWeeklyConsumption}
            onChange={(e) => {
              const v = e.target.value;
              setAvgWeeklyConsumption(v === '' ? '' : Number(v));
            }}
          />
          <p className="text-xs text-neut-500 mt-1">
            {t('inventory.average_consumption_subscript')}
          </p>
        </div>
      </div>

      <div className="px-6 py-4 border-t border-neut-200 flex items-center justify-end gap-3">
        <Button
          variant={'secondary'}
          onClick={() => setShowAddInventory(false)}
        >
          {t('inventory.cancel')}
        </Button>
        <Button disabled={!canSubmit} onClick={onAddInventory}>
          {t('inventory.add')}
        </Button>
      </div>
    </div>
  );
}
